import { normalizeEquipmentGroupSection } from '../formStepUtils';

/**
 * Build the INITIAL nested equipment blob for one `equipmentGroupSection`
 * item (SOF-3402/SOF-3439).
 *
 * `equipmentGroupSection` is registered `kind: 'custom'` with NO
 * `collectChildFields` (vulcanContentTypeMap.js) — self-contained sub-bag
 * mode, by design (its state is genuinely nested, `{productKey: {value, ...}}`,
 * not a flat leaf field). But that also means the forms-core engine's OWN
 * initial-value/field-state builders (`buildInitialValues`/`buildFieldStates`
 * in the package) never walk into it at all — they only ever see `kind:
 * 'field'` items reachable via `.items`, which a bare `custom` item doesn't
 * have. So `form.values[sectionKey]` was simply `undefined` for a brand-new
 * session (only populated at all if a PRE-EXISTING saved order's `orderData`
 * happened to already carry that key into `initialValues`).
 *
 * The legacy render loop never had this gap: `formStepVulcan.js`'s
 * `normalizedFields` called `normalizeEquipmentGroupSection` inline while
 * walking the WHOLE Contentful step tree, for every equipment section,
 * building the nested blob before first render — but that seeding step was
 * never carried into the new adapter layer when the equipment section was
 * migrated onto the engine. `EquipmentSectionForm` gates every item list on
 * `formFields[name] && ...` (`name` = the section's own key) — headings
 * render unconditionally above those gates, so with an unseeded blob the
 * section shows its title with NOTHING underneath. Confirmed real-data bug.
 *
 * This originally ported formStepVulcan.js's legacy `data1`-selection
 * if/else-if/else verbatim (pick ONE of formStepFields+easyPaymentFields OR
 * the three payment-tier arrays). That's unsafe here: `EquipmentSectionForm`
 * renders each of formStepFields/easyPaymentFields/oneTimePaymentFields/
 * threeYearEasyPaymentFields/fiveYearEasyPaymentFields INDEPENDENTLY, gated
 * only on `Array.isArray(list)` for THAT list — not on which one this
 * function picked. A section with BOTH formStepFields and the payment-tier
 * arrays populated at once rendered formStepFields's products unconditionally
 * while this function silently excluded them from the seeded blob (the tiered
 * branch won instead). Confirmed real-data crash: "[equipmentGroupSection]
 * Cannot read properties of undefined (reading 'isVisible')" reading
 * `formFields[name][fields.name]` for a product the blob never got.
 *
 * So this seeds the UNION of every list that's actually an array, matching
 * what the component actually renders, rather than picking one.
 *
 * @param {Object} raw  The equipmentGroupSection entry's Contentful `fields` (`item.raw`)
 * @param {{isEditing?: boolean, orderData?: Object}} [ctx]
 * @returns {Object}  The nested blob (`{productKey: {value, isVisible, ...}, ...}`)
 */
export const buildInitialEquipmentBlob = (
  raw,
  { isEditing, orderData } = {}
) => {
  if (!raw) return {};
  const sources = [
    raw.formStepFields,
    raw.easyPaymentFields,
    raw.oneTimePaymentFields,
    raw.threeYearEasyPaymentFields,
    raw.fiveYearEasyPaymentFields
  ].filter(Array.isArray);
  const data = sources.length ? [].concat(...sources) : undefined;
  return normalizeEquipmentGroupSection({
    data,
    isEditing,
    orderData,
    key: raw.name
  });
};

export default buildInitialEquipmentBlob;
